import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card } from './Card';
import { useSystemHealth } from '../hooks/useSystemHealth';

function StatusDot({ online }: { online: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: online ? '#00a65a' : '#dc3545',
        marginRight: '4px',
      }}
    />
  );
}

function StatusBadge({ online }: { online: boolean }) {
  // inline lookup — cannot use useTranslation() outside component
  const label = online ? 'Online' : 'Offline';
  return (
    <span className={`badge badge-${online ? 'success' : 'danger'}`}>
      <StatusDot online={online} />
      {label}
    </span>
  );
}

function SkeletonBar({ width = '100%' }: { width?: string }) {
  return (
    <div
      style={{
        height: '12px',
        width,
        background: '#e9ecef',
        borderRadius: '4px',
        marginBottom: '8px',
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

export function SystemHealthCard() {
  const { t } = useTranslation();
  const { runners, servers, summary, isLoading, error } = useSystemHealth();

  const worstStatus =
    summary.offlineRunners > 0 || summary.offlineServers > 0
      ? 'danger'
      : summary.degradedRunners > 0 || summary.degradedServers > 0
      ? 'warning'
      : 'success';

  const healthColor =
    worstStatus === 'danger'
      ? '#dc3545'
      : worstStatus === 'warning'
      ? '#ffc107'
      : '#00a65a';

  return (
    <Card title={t('dashboard.system_health')} collapsible className="h-100">
      {error ? (
        <div className="text-danger">
          <i className="bi bi-exclamation-triangle mr-1" />
          {error}
        </div>
      ) : isLoading ? (
        <div>
          <SkeletonBar width="60%" />
          <SkeletonBar width="80%" />
          <SkeletonBar width="50%" />
        </div>
      ) : (
        <>
          {/* Summary bar */}
          <div className="mb-3">
            <span style={{ color: healthColor, fontWeight: 600 }}>
              <StatusDot online={summary.offlineRunners === 0} />
              {t('common.online')}: {summary.onlineRunners}
            </span>
            <span className="mx-2 text-muted">|</span>
            <span style={{ color: summary.offlineRunners > 0 ? '#dc3545' : undefined }}>
              <StatusDot online={false} />
              {t('common.offline')}: {summary.offlineRunners}
            </span>
            {(summary.degradedRunners > 0 || summary.degradedServers > 0) && (
              <>
                <span className="mx-2 text-muted">|</span>
                <span className="text-warning">
                  {t('common.degraded')}: {summary.degradedRunners + summary.degradedServers}
                </span>
              </>
            )}
          </div>

          {/* Runner list */}
          {runners.length > 0 && (
            <div className="mb-3">
              <p className="text-muted mb-1" style={{ fontSize: '12px' }}>
                <strong>{t('navigation.tool_runners')}</strong>
              </p>
              {runners.map((runner) => (
                <div
                  key={runner.id}
                  className="d-flex justify-content-between align-items-center mb-1"
                >
                  <span style={{ fontSize: '13px' }}>{runner.name}</span>
                  <StatusBadge online={runner.isOnline} />
                </div>
              ))}
            </div>
          )}

          {/* Server list */}
          {servers.length > 0 && (
            <div className="mb-3">
              <p className="text-muted mb-1" style={{ fontSize: '12px' }}>
                <strong>{t('navigation.media_servers')}</strong>
              </p>
              {servers.map((server) => (
                <div
                  key={server.id}
                  className="d-flex justify-content-between align-items-center mb-1"
                >
                  <span style={{ fontSize: '13px' }}>{server.name}</span>
                  <StatusBadge online={server.isOnline} />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {runners.length === 0 && servers.length === 0 && (
            <p className="text-muted mb-0">{t('dashboard.no_infrastructure')}</p>
          )}

          {/* Link */}
          <div className="mt-2">
            <Link to="/admin/infrastructure" className="text-sm">
              {t('common.view_infrastructure')} → <i className="bi bi-arrow-right" />
            </Link>
          </div>
        </>
      )}
    </Card>
  );
}
